import { useNavigate } from "react-router-dom"
import { gql } from "~/__generated__"
import { useViewer } from "~/auth/use-viewer"
import { useDocumentTitle } from "~/common/use-document-title"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { setupLettermemePath, settingsPath } from "~/common/paths"
import { LettermemeForm, LettermemeFormValues } from "./lettermeme-form"
import { getMetaContent } from "~/common/get-meta-content"

const createLettermemeMutation = gql(/* GraphQL */ `
  mutation CreateLettermeme($input: LettermemeCreateInput!) {
    lettermemeCreate(input: $input) {
      lettermeme {
        id
        name
        urlPath
        shareableEmailIn
        publicDescription
        emailHeaderText
        summaryTonePrompt
        cartoonStylePrompt
        emailCadence
        updateCadence
        privacy
        generateCartoons
      }
    }
  }
`)

const DEFAULT_CARTOON_STYLE_PROMPT = getMetaContent("default-cartoon-style-prompt")

export const CreateLettermemeScreen: React.FC = () => {
  const navigate = useNavigate()
  const { viewer } = useViewer()

  useDocumentTitle("Create Lettermeme")

  const [createLettermeme, mutationResult] = useSafeMutation(createLettermemeMutation, {
    onCompleted: (data) => {
      const lettermemeId = data.lettermemeCreate?.lettermeme?.id
      if (lettermemeId) {
        navigate(setupLettermemePath({ id: lettermemeId }))
      }
    },
  })
  const { loading: createLoading } = mutationResult

  const onSubmit = (values: LettermemeFormValues) => {
    createLettermeme({
      variables: {
        input: {
          lettermemeInput: {
            name: values.name,
            urlPath: values.urlPath,
            shareableEmailIn: values.shareableEmailIn,
            publicDescription: values.publicDescription,
            emailHeaderText: values.emailHeaderText,
            summaryTonePrompt: values.summaryTonePrompt,
            cartoonStylePrompt: values.cartoonStylePrompt,
            emailCadence: values.emailCadence,
            updateCadence: values.updateCadence,
            userEmails: values.userEmails,
            privacy: values.privacy,
            generateCartoons: values.generateCartoons === "true",
          },
        },
      },
    })
  }

  return (
    <div className="mx-auto mt-10 max-w-3xl p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Create Lettermeme</h1>
        <p className="text-gray-400">Set up your new lettermeme</p>
      </div>

      <LettermemeForm
        defaultValues={{
          name: "",
          urlPath: "",
          shareableEmailIn: "",
          publicDescription: "",
          emailHeaderText: "",
          summaryTonePrompt: "",
          cartoonStylePrompt: DEFAULT_CARTOON_STYLE_PROMPT,
          emailCadence: "WEEKLY",
          updateCadence: "DAILY",
          userEmails: [],
          privacy: "HIDDEN",
          generateCartoons: "true",
        }}
        ownerEmails={[viewer.email]}
        onSubmit={onSubmit}
        isSubmitting={createLoading}
        submitLabel="Create Lettermeme"
        onCancel={() => navigate(settingsPath({}))}
        mutationResult={mutationResult}
      />
    </div>
  )
}
